import Polyglot from 'node-polyglot';
import { SensordataNewestMixed } from '../../../../monitoring/api/monitoring.model';
import {
  DEFAULT_DECIMALS,
  DEFAULT_EVENT_ON,
  DEFAULT_UNIT_SENSOR,
  DEFAULT_VALUE_SENSOR,
} from '../constants';
import { DeviceControlUnitsChannelMetadataAPIResponse } from '../../../../commander/api/device.model';
import _ from 'lodash';
import {
  celsiusToFahrenheit,
  fahrenheitToCelsius,
  relativeFahrenheitToKelvin,
} from '../../../../../customized/fresh-watch/theme/utils/temperatureUtils';

/**
 * Sensor value to show in web, extracted from sensorValueToShowInWeb
 * This function returns the value to be shown in the web frontend by a sensor
 * If the field value = false then the DEFAULT_VALUE_SENSOR will be shown
 * If the value is not undefined and not null and is numeric then the value is shown
 * TODO: If the known mapped error as systemErrorId show it
 * In other case the DEFAULT_VALUE_SENSOR is shown.
 * @param object controlUnit
 * @param object sensor
 * @return float/string value
 */
export function getValueStrFromSensor(
  sensor: SensordataNewestMixed | undefined,
  isOn: boolean | undefined,
  isConnected: boolean | undefined,
  polyglot: Polyglot,
  preferredTemperatureUnit: string
) {
  if (!isConnected) return polyglot.t('device.controlUnit.offline');
  // value is not available yet
  if (isOn === undefined) return DEFAULT_VALUE_SENSOR;

  // to show off value
  if (isOn === false) return polyglot.t('general.off');

  // When there is no sensor information but the device is on, we want to show an "on" state instead of an unknown value
  if (sensor === undefined && isOn) return DEFAULT_EVENT_ON;
  // sensor in invalid state
  if (sensor?.state !== 0) return DEFAULT_VALUE_SENSOR;
  // value of sensor unknown
  if (typeof sensor?.value != 'number') return DEFAULT_VALUE_SENSOR;

  return getValueWithPreferredUnitAndRounded(
    sensor.value,
    sensor?.metadata,
    preferredTemperatureUnit
  );
}

export function getValueWithPreferredUnitAndRounded(
  vale: number,
  metadata: DeviceControlUnitsChannelMetadataAPIResponse | undefined,
  preferredTemperatureUnit: string
) {
  const value = getValueWithPreferredUnit(
    vale,
    metadata?.unitString ?? metadata?.unit,
    preferredTemperatureUnit
  );

  return getValueRounded(value, metadata, preferredTemperatureUnit);
}

export function getValueRounded(
  value: number,
  metadata:
    | DeviceControlUnitsChannelMetadataAPIResponse
    | {
        decimals?: number;
        unitString?: string | undefined | null;
        unit?: string | undefined | null;
      }
    | undefined,
  preferredTemperatureUnit: string
) {
  const unitPreferred = getPreferredUnit(
    metadata?.unitString ?? metadata?.unit,
    preferredTemperatureUnit
  );

  const decimals = getDecimals(metadata?.decimals, unitPreferred);
  return _.round(value, decimals);
}

export function getDecimals(
  decimals: number | undefined,
  unitPreferred: string | undefined | null
) {
  if (unitPreferred === '°F' && decimals == 1) {
    // conversion issues, decide to show no decimals
    return 0;
  }
  return decimals ?? DEFAULT_DECIMALS;
}

export function getUnitStrFromSensor(
  sensor: SensordataNewestMixed | undefined,
  isOn: boolean | undefined,
  preferredTemperatureUnit: string
) {
  // to show off value
  if (isOn === false) return '';

  const unit = getPreferredUnit(
    sensor?.metadata?.unitString ?? sensor?.metadata?.unit,
    preferredTemperatureUnit
  );

  return unit ?? '';
}

export function getPreferredUnit(
  defaultUnit?: string | undefined | null,
  preferredTemperatureUnit?: string
) {
  if (defaultUnit == 'NON') {
    return '';
  }
  if (defaultUnit == 'DEGREECELCIUS') {
    defaultUnit = '°C';
  }
  if (defaultUnit == 'DEGREEFAHRENHEIT') {
    defaultUnit = '°F';
  }
  if (defaultUnit == 'KELVIN') {
    defaultUnit = 'K';
  }
  if (defaultUnit == 'BINARY') {
    defaultUnit = '';
  }
  if (defaultUnit !== '°C' && defaultUnit !== '°F' && defaultUnit !== 'K') {
    return defaultUnit;
  }
  const unit = preferredTemperatureUnit ?? defaultUnit ?? DEFAULT_UNIT_SENSOR;
  return unit;
}

export function getValueWithPreferredUnit(
  value: number,
  defaultUnit?: string | undefined | null,
  preferredTemperatureUnit?: string
) {
  if (value === undefined || value === null) {
    return value;
  }
  let from = defaultUnit;
  if (from === 'DEGREECELCIUS') from = '°C';
  if (from === 'DEGREEFAHRENHEIT') from = '°F';
  if (from === 'KELVIN') from = 'K';
  const to = getPreferredUnit(defaultUnit, preferredTemperatureUnit);

  // °C to °F
  if (from === '°C' && to === '°F') return celsiusToFahrenheit(value);
  // °F to °C
  if (from === '°F' && to === '°C') return fahrenheitToCelsius(value);
  // K to °F
  if (from === 'K' && to === '°F') return relativeFahrenheitToKelvin(value);
  // No conversion needed
  return value;
}

export function getValueWithPreferredUnitAndDecimals(
  value: number | undefined | null,
  defaultUnit: string | undefined | null,
  decimals: number | undefined,
  preferredTemperatureUnit: string
) {
  if (value === undefined || value === null) {
    return {
      strValue: DEFAULT_VALUE_SENSOR,
      value: DEFAULT_VALUE_SENSOR,
      unit: '',
    };
  }
  const unit = getPreferredUnit(defaultUnit, preferredTemperatureUnit);
  const valueSensor = getValueWithPreferredUnit(
    value,
    defaultUnit,
    preferredTemperatureUnit
  );
  const valueRounded =
    typeof valueSensor === 'number' &&
    getValueRounded(
      valueSensor,
      {
        decimals: decimals,
        unitString: defaultUnit,
      },
      preferredTemperatureUnit
    );
  const valueFixed =
    typeof valueSensor === 'number' ? valueRounded : valueSensor;
  return {
    strValue: `${valueFixed}${unit ?? ''}`,
    value: `${valueFixed}`,
    unit: unit ?? '',
  };
}
